import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, X, ArrowUpRight } from "lucide-react";
import { IMG } from "@/lib/images";
import { track } from "@/lib/track";

const NAV = [
  { label: "Our Story", to: "/our-story" },
  { label: "Menu", to: "/menu" },
  { label: "Ripe Now", to: "/ripe-now" },
  { label: "Stores", to: "/stores" },
  { label: "Franchise", to: "/franchise" },
];

const OWN_A_RIFIC = { label: "OWN A RIFIC", to: "/franchise#franchise-form" };

const goOwnARific = () => track("franchise_cta_click", { placement: "header" });

export const Logo = ({ light = false }) => (
  <Link
    to="/"
    data-testid="nav-logo"
    className="inline-flex items-center"
    aria-label="RIFIC home"
  >
    <img
      src={light ? IMG.logoLight : IMG.logo}
      alt="RIFIC — Real Fruit Ice Cream"
      className="h-9 w-auto md:h-10"
      fetchPriority="high"
    />
  </Link>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-cream-line bg-cream/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1440px] items-center justify-between px-5 md:h-20 md:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-body text-[13px] font-semibold uppercase tracking-[0.14em] text-deep/80 transition-colors hover:text-orange-rific"
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to={OWN_A_RIFIC.to}
            data-testid="nav-own-a-rific-btn"
            onClick={goOwnARific}
            className="hidden items-center gap-2 rounded-full bg-orange-rific px-6 py-3 font-body text-xs font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-deep md:inline-flex"
          >
            {OWN_A_RIFIC.label}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="grid h-11 w-11 place-items-center rounded-full border border-deep/15 text-deep lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto bg-deep px-6 pb-24 pt-10 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center justify-between border-b border-deep-line py-4 font-display text-4xl font-semibold text-cream"
                  >
                    {item.label}
                    <ArrowUpRight className="h-6 w-6 text-orange-rific" />
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="pt-8"
              >
                <Link
                  to={OWN_A_RIFIC.to}
                  data-testid="mobile-own-a-rific-btn"
                  onClick={goOwnARific}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-orange-rific px-8 py-4 font-body text-sm font-bold uppercase tracking-[0.16em] text-white"
                >
                  {OWN_A_RIFIC.label}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
